// @ts-strict-ignore
import type { FetchResult, InMemoryCache } from "@apollo/client";
import { faCamera } from "@fortawesome/pro-regular-svg-icons";
import type { ChangeEvent, JSX } from "react";
import { useReducer, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { AviaryAvatarSize } from "@aviary";
import {
  Avatar,
  AvatarBadge,
  Button,
  Typography,
  Message,
  Modal,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  Arrange,
} from "@aviary";
import { CircleLoader } from "@shared/components/CircleLoader/CircleLoader";
import { ImageCrop } from "@shared/components/ImageCrop/ImageCrop";
import type { UserAvatarCreateData } from "@shared/data/mutations/UserAvatarCreateMutation/UserAvatarCreate.mutation";
import type { UserAvatarDeleteData } from "@shared/data/mutations/UserAvatarDeleteMutation/UserAvatarDelete.mutation";
import { useCurrentPractitionerQuery } from "@shared/data/queries/CurrentPractitionerQuery/CurrentPractitioner.query";
import type { UserProfileQueryData } from "@shared/data/queries/UserProfileQuery/UserProfile.query";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { l } from "@shared/locales/i18n";
import { AvatarSize } from "@shared/types/avatarSize";

import { MultiplePlatformWarning } from "../MultiplePlatformWarning/MultiplePlatformWarning";

import { UserAvatarForPersonalProfileCard_Shared_Query } from "./UserAvatar.query";
import { useAvatarHooks } from "./useAvatarHooks";

import * as styles from "./ProfileCardAvatar.styles";

interface Props {
  id: string;
  isSize?: AviaryAvatarSize;
  isBadgeHidden?: boolean;
  uploadButtonText?: string;
  onUpdate?: (cache: InMemoryCache, data: FetchResult<UserAvatarCreateData>) => void;
  onRemove?: (cache: InMemoryCache) => void;
  readonly?: boolean;
}

interface ImageState {
  uploadedImage: string;
  avatarUrl: string;
  avatarId?: string;
  firstName?: string;
  lastName?: string;
}

const ProfileCardAvatar = ({
  id,
  isSize,
  isBadgeHidden,
  uploadButtonText,
  onUpdate,
  onRemove,
  readonly,
}: Props) => {
  const initialImageState: ImageState = {
    uploadedImage: null,
    avatarUrl: null,
  };

  const { t } = useTranslation();
  const fileRef = useRef(null);
  const { tablet } = useBreakpoints();
  const isMobile = tablet.lessThan;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCropFinalized, setIsCropFinalized] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ fields: object; message: string }>({
    fields: {},
    message: "",
  });

  const [imageState, setImageState] = useReducer(
    (currentState: ImageState, newState: ImageState) => ({ ...currentState, ...newState }),
    initialImageState
  );

  const onAvatarQuery = (userWithAvatarData: UserProfileQueryData) => {
    setImageState({
      uploadedImage: null,
      avatarId: userWithAvatarData?.viewer?.user?.avatar?.id,
      avatarUrl: userWithAvatarData?.viewer?.user?.avatar?.photoUrl,
      firstName: userWithAvatarData?.viewer?.user?.firstName,
      lastName: userWithAvatarData?.viewer?.user?.lastName,
    });
  };

  const { data: userProfileData } = useCurrentPractitionerQuery();
  const hasMultiPlatformWarning = userProfileData?.currentPractitioner?.hasMultipleStorePlatforms;

  const onAvatarUploadComplete = (data: UserAvatarCreateData): void => {
    const { fields, message } = data?.userAvatarCreate?.errors;

    if (fields || message) {
      setErrors({ fields, message });

      return;
    }

    const {
      userAvatarCreate: { avatar },
    } = data;

    setIsModalOpen(false);
    setErrors({ fields: {}, message: "" });
    setImageState({
      ...initialImageState,
      avatarId: avatar.id,
      avatarUrl: avatar.photoUrl,
    });
  };

  const onAvatarDeleteComplete = (data: UserAvatarDeleteData): void => {
    const { fields, message } = data?.userAvatarDelete?.errors;

    if (fields || message) {
      setErrors({ fields, message });

      return;
    }

    setImageState({ ...imageState, avatarUrl: null, avatarId: null });
    setIsCropFinalized(false);
    setIsModalOpen(false);
  };

  /** Custom Hook to get all the GraphQL operations pertaining to user avatars */
  const { userAvatarCreate, userAvatarDelete, loading } = useAvatarHooks(
    onAvatarUploadComplete,
    onAvatarDeleteComplete,
    onAvatarQuery,
    setErrors,
    errors
  );

  const closeModal = (): void => {
    if (!isCropFinalized) {
      setImageState({
        ...initialImageState,
        avatarUrl: imageState.avatarUrl,
      });
    }

    setErrors({ fields: {}, message: "" });
    setIsModalOpen(false);
  };

  const changePhoto = (): void => {
    setIsModalOpen(false);
    fileRef.current.click();
  };

  const newAvatarSelection = () => {
    if (imageState.avatarUrl) {
      setIsModalOpen(true);
    } else {
      fileRef.current.click();
    }
  };

  const handleImageUpload = (e: ChangeEvent<HTMLInputElement>): void => {
    if (e.target.files && e.target.files.length > 0) {
      const reader: FileReader = new FileReader();
      reader.addEventListener(
        "load",
        () => {
          fileRef.current.value = ""; // reset the value for original upload to allow same image uploads

          setIsModalOpen(true);
          setImageState({
            ...imageState,
            uploadedImage: reader.result.toString(),
          });
        },
        { capture: true }
      );

      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const handleCreate = (imgFile: File) =>
    onUpdate
      ? userAvatarCreate({
          variables: {
            input: {
              attributes: {
                photo: imgFile,
              },
            },
            filter: AvatarSize.LARGE,
          },
          refetchQueries: [
            {
              query: UserAvatarForPersonalProfileCard_Shared_Query,
              variables: {
                filter: AvatarSize.SMALL,
              },
            },
          ],
          update: (cache: InMemoryCache, result) => onUpdate(cache, result),
        })
      : userAvatarCreate({
          variables: {
            input: {
              attributes: {
                photo: imgFile,
              },
            },
            filter: AvatarSize.LARGE,
          },
          refetchQueries: [
            {
              query: UserAvatarForPersonalProfileCard_Shared_Query,
              variables: {
                filter: AvatarSize.SMALL,
              },
            },
          ],
        });

  const handleSaveCroppedImage = (imgFile: File): void => {
    handleCreate(imgFile);
    setIsCropFinalized(false);
  };

  const renderError = () => (
    <Message isColor="danger">
      <Typography type="h3">{t(l.sharedSettings.profile.avatar.AvatarError)}</Typography>
    </Message>
  );

  const isError = () => Object.keys(errors.fields).length > 0 || errors.message;

  const renderAvatarOrUploadedImage = () => {
    const { avatarUrl, uploadedImage } = imageState;

    if (uploadedImage) {
      return (
        <div css={styles.imgContainer}>
          <ImageCrop
            id={id}
            src={imageState.uploadedImage}
            isCropFinalized={isCropFinalized}
            onError={message => setErrors({ ...errors, message })}
            onSave={handleSaveCroppedImage}
          />
        </div>
      );
    }

    if (avatarUrl) {
      return (
        <div css={styles.image}>
          <img src={avatarUrl} />
        </div>
      );
    }

    return null;
  };

  const renderChangeButton = () => (
    <Button
      key="change"
      isOutlined
      css={styles.leftButton}
      onClick={changePhoto}
      aria-label={t(l.sharedSettings.profile.avatar.UploadNewPhoto)}
    >
      {t(l.sharedSettings.profile.avatar.UploadNew)}
    </Button>
  );

  const renderSaveButton = () => (
    <Button
      key="save"
      isLoading={loading.avatarCreateLoading}
      disabled={loading.avatarCreateLoading}
      intention="primary"
      onClick={() => setIsCropFinalized(true)}
      aria-label={t(l.sharedSettings.profile.avatar.SavePhoto)}
      data-e2e="avatar-upload-submit"
    >
      {t(l.sharedSettings.profile.avatar.SavePhoto)}
    </Button>
  );

  const handleDelete = () =>
    onRemove
      ? userAvatarDelete({
          variables: {
            input: {
              userAvatarId: imageState.avatarId,
            },
          },
          update: (cache: InMemoryCache) => onRemove(cache),
        })
      : userAvatarDelete({
          variables: {
            input: {
              userAvatarId: imageState.avatarId,
            },
          },
        });

  const renderDeleteButton = () => (
    <Button
      key="delete"
      intention="danger"
      isLoading={loading.avatarDeleteLoading}
      disabled={loading.avatarDeleteLoading}
      aria-label={t(l.sharedSettings.profile.avatar.RemoveAvatar)}
      onClick={handleDelete}
    >
      {t(l.sharedSettings.profile.avatar.RemoveAvatar)}
    </Button>
  );

  const renderModalButtons = (): JSX.Element[] => {
    const { avatarUrl, uploadedImage } = imageState;

    return [
      renderChangeButton(),
      ...(uploadedImage ? [renderSaveButton()] : []),
      ...(!uploadedImage && avatarUrl ? [renderDeleteButton()] : []),
    ];
  };

  const renderModal = () => {
    return (
      <Modal isOpen={isModalOpen} onClose={closeModal} size={isMobile ? "small" : "medium"}>
        <ModalContainer>
          {isError() && renderError()}
          <ModalHeader>
            <div css={styles.headerStyles}>
              <Typography
                type="h4"
                css={styles.title}
                aria-label={t(l.sharedSettings.profile.avatar.EditAvatarPhoto)}
              >
                {t(l.sharedSettings.profile.avatar.EditAvatarPhoto)}
              </Typography>
            </div>
          </ModalHeader>
          {renderAvatarOrUploadedImage()}
          <ModalFooter css={styles.buttonContainer}>
            <MultiplePlatformWarning
              isMultiplatform={hasMultiPlatformWarning}
              childrenStyles={styles.children}
            >
              {renderModalButtons()}
            </MultiplePlatformWarning>
          </ModalFooter>
        </ModalContainer>
      </Modal>
    );
  };

  const { avatarUrl, firstName, lastName } = imageState;

  if (loading.userProfileLoading) {
    return <CircleLoader />;
  }

  return (
    <div css={[styles.profileCardAvatar, isBadgeHidden && styles.profileCardAvatarNoBadge]}>
      <Arrange
        justify="spaceBetween"
        alignment={isMobile ? "start" : "center"}
        isVertical={isMobile}
        isFullWidth={isMobile}
        isWrapped={false}
      >
        <Avatar
          image={avatarUrl}
          firstName={firstName}
          lastName={lastName}
          label={t(l.sharedSettings.profile.avatar.AvatarFor, { firstName, lastName })}
          size={isSize || "small"}
          isLight
          overlayPosition="bottomRight"
        >
          {!isBadgeHidden && (
            <AvatarBadge
              icon={faCamera}
              aria-label={t(l.sharedSettings.profile.avatar.UploadNewPhoto)}
              onClick={newAvatarSelection}
            />
          )}
        </Avatar>
        {!!isBadgeHidden && !readonly && (
          <Arrange justify="end" isFullWidth={isMobile}>
            <Button
              intention="system"
              size="small"
              onClick={newAvatarSelection}
              data-e2e="avatar-upload"
              isFullWidth={isMobile}
            >
              {t(uploadButtonText || l.sharedSettings.profile.avatar.EditAvatar)}
            </Button>
            {!!avatarUrl && (
              <Button intention="system" size="small" onClick={handleDelete} isFullWidth={isMobile}>
                {t(l.sharedSettings.profile.avatar.RemoveAvatar)}
              </Button>
            )}
          </Arrange>
        )}
      </Arrange>

      {/* Hidden input field for file upload */}
      {!readonly && (
        <input
          accept="image/jpeg, image/jpg, image/png"
          hidden
          ref={fileRef}
          type="file"
          onChange={handleImageUpload}
          data-e2e="avatar-upload-input"
        />
      )}
      {!!isModalOpen && renderModal()}
    </div>
  );
};

export { ProfileCardAvatar };
